import { useState, useEffect, useRef } from "react";

import _ from "lodash";
import toast from "react-hot-toast";
import styled from "styled-components";

import { DECLINE_TRANSACTION_OPTIONS } from "../../types";

import { trackAction } from "~/analytics";
import { ButtonLink } from "~/components/Button";
import { Separator } from "~/components/Elements";
import * as Form from "~/components/Form";
import Loading from "~/components/Loading";
import Modal from "~/components/Modal";
import { RadioButton } from "~/components/RadioButton";
import Spacer, { Axis } from "~/components/Spacer";
import { Title } from "~/components/Text";
import { postTxDecline, postTxBulk } from "~/endpoints/transactions";
import {
  Transaction,
  TransactionStatus,
  TransactionDeclineRequest,
  TransactionType,
} from "~/endpoints/transactions/types";
import { useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import { TransactionWrap } from "~/hooks/use-transaction";
import ConfirmIncomingTxModal from "~/templates/transactions/components/ConfirmIncomingTxModal";
import IncomingTxDisplay from "~/templates/transactions/components/IncomingTxDisplay";

const FlexDiv = styled.div`
  display: flex;
  justify-content: center;
`;
const CenteredText = styled.div`
  text-align: center;
`;
const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ReasonWarning = styled.div`
  font-size: 12px;
`;

const CustomReasonInput = styled.input`
  height: 25px;
  width: 80%;
  border: 1px solid #babcba;
  border-radius: 4px;
  margin-left: 25px;
`;

type DeclineIncomingTxModalProps = {
  transaction: TransactionWrap;
  onClose: () => void;
};

export default function DeclineIncomingTxModal({ transaction, onClose }: DeclineIncomingTxModalProps) {
  const [isBulk, setIsBulk] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBlockchainAddressConfirmationNeeded, setIsBlockchainAddressConfirmationNeeded] = useState<
    boolean | undefined
  >(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const trackProps = transaction?.beneficiaryVASPdid ? { group_vasp: transaction.beneficiaryVASPdid } : undefined;

  const { data: externalVASP, isLoading: loadingVaspInfo } = useTfSimpleVaspInfo(transaction.originatorVASPdid);

  useEffect(() => {
    if (reason === "Other") {
      inputRef?.current?.focus();
    }
  }, [reason]);

  useEffect(() => {
    if (transaction) {
      setIsBlockchainAddressConfirmationNeeded(transaction.status === TransactionStatus.SENT);
    }
  }, [transaction]);

  const handleOnAddressConfirmed = (updatedTransaction: Transaction) => {
    setIsBlockchainAddressConfirmationNeeded(false);
    const wasTransactionAutoAccepted = updatedTransaction.status === TransactionStatus.ACCEPTED;
    const wasTransactionAutoDeclined = updatedTransaction.status === TransactionStatus.DECLINED;
    if (wasTransactionAutoAccepted) {
      // we don't need to show the Accept Modal in this case
      toast.success("Transfer was auto-accepted and moved to COMPLETED");
      trackAction("transaction-was-auto-accepted", trackProps);
      onClose();
    }
    if (wasTransactionAutoDeclined) {
      // we don't need to show the Accept Modal in this case
      toast.success("Transfer was auto-declined and moved to COMPLETED");
      trackAction("transaction-was-auto-declined", trackProps);
      onClose();
    }
  };

  const declineTransaction = async (declineReason: string) => {
    /*This is the regular flow for our customers */

    if (isBulk) {
      const transactions = await postTxBulk({
        action: "decline",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
        reason: declineReason,
      });
      toast.success(`${transactions.length} transfers declined and moved to COMPLETED`);
      transactions.forEach(() => trackAction("user-declined-incoming-transaction", trackProps));
    } else {
      const payload: TransactionDeclineRequest = {
        id: transaction.id,
        reason: declineReason,
      };
      await postTxDecline(payload);
      toast.success("Transfer declined and moved to COMPLETED");
      trackAction("user-declined-incoming-transaction", trackProps);
    }
  };

  const handleDeclineTx = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      if (transaction) {
        await declineTransaction(reason !== "Other" ? reason : customReason);
      }
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
    }
    setIsLoading(false);
    onClose();
  };

  if (!transaction || isBlockchainAddressConfirmationNeeded === undefined || loadingVaspInfo) {
    return <Loading />;
  }

  if (isBlockchainAddressConfirmationNeeded === true) {
    return (
      <ConfirmIncomingTxModal
        transaction={transaction}
        onAddressConfirmed={(updatedTransaction) => {
          handleOnAddressConfirmed(updatedTransaction);
        }}
        onClose={onClose}
      />
    );
  }

  if (
    transaction &&
    isBlockchainAddressConfirmationNeeded === false &&
    (externalVASP || _.isNil(transaction.originatorVASPdid))
  ) {
    const originatorVaspName = externalVASP?.name;

    return (
      <Modal active onClose={onClose}>
        <Modal.Header>
          <Title as="h1" align="centered">
            {"Decline this Travel Rule transfer"}
          </Title>
        </Modal.Header>
        <Modal.Body
          styles={{
            maxWidth: 1000,
          }}
        >
          {isLoading && <Loading />}

          <CenteredText>{"Are you sure you want to decline the Travel Rule data transfer?"}</CenteredText>

          <Spacer axis={Axis.VERTICAL} size={20} />
          <FlexDiv>
            <IncomingTxDisplay
              customerLogo={transaction.beneficiaryVASP?.logo}
              customerDisplayText={transaction.beneficiaryDisplay}
              counterpartyLogo={transaction.originatorVASP?.logo}
              counterpartyDisplayText={transaction.originatorDisplay}
              counterPartyVASP={externalVASP}
            />
          </FlexDiv>
          <Spacer axis={Axis.VERTICAL} size={40} />
          {"Please let us know why you want to decline this transfer (optional):"}
          <RadioButton
            values={DECLINE_TRANSACTION_OPTIONS}
            onChangeValue={(e) => {
              setReason(e.target.value);
              setCustomReason("");
            }}
          />
          <CustomReasonInput
            aria-label="customReason"
            ref={inputRef}
            value={customReason}
            disabled={reason !== "Other"}
            onChange={(e) => setCustomReason(e.target.value)}
          />
          <Spacer axis={Axis.VERTICAL} size={12} />
          <ReasonWarning>{"This information will be accessible by your counterparty"}</ReasonWarning>

          <Separator color="link" weight="light" />
          {transaction.type === TransactionType.TRAVELRULE && externalVASP && (
            <Form.CheckboxLabel>
              <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
              <Form.CheckboxText>
                Decline all data transfers from <b>{originatorVaspName || externalVASP?.did}</b> remaining in the Inbox
              </Form.CheckboxText>
            </Form.CheckboxLabel>
          )}
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink onClick={onClose}>{"Take me back"}</ButtonLink>
            <ButtonLink
              color="danger"
              disabled={!reason || (reason === "Other" && customReason === "")}
              onClick={handleDeclineTx}
            >
              Decline this transfer
            </ButtonLink>
          </SpaceBetween>
        </Modal.Footer>
      </Modal>
    );
  }

  return <Loading />;
}
